import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import Layout from './components/Layout';
import { API_BASE_URL } from '../config/ApiConfig';
import { useNavigate } from 'react-router-dom';
import TableContainer from './components/TableContainer';
import Pagination10 from './components/Pagination10';
import useDefaultModal from './components/hooks/useDefaultModal';
import useConfirmModal from './components/hooks/useConfirmModal';
import ConfirmModal from './components/modals/ConfirmModal';
import DefaultModal from './components/modals/DefaultModal';
import ResponsiveShadow from './components/ResponsiveShadow';
import usePagination10 from './components/hooks/usePagination10';
import useParametrosEntidade from './components/hooks/useParametrosEntidade';
import PageHeader from './components/PageHeader';
import PageContent from './components/PageContent';
import CompraDiretaModal from './components/modals/CompraDiretaModal';
import CompraDiretaItensModal from './components/modals/CompraDiretaItensModal';
import PreEmpenhoModal from './components/modals/PreEmpenhoModal';
import LoadingModal from './components/modals/LoadingModal';
import DataTable from './components/DataTable';
import { Dropdown } from 'react-bootstrap';
import useAsyncPdf from './components/hooks/useAsyncPdf';

function ComprasDiretas() {
  const [compras, setCompras] = useState([]);
  const [filtro, setFiltro] = useState('');
  const [atualizar, setAtualizar] = useState(false);
  const [compraSelecionada, setCompraSelecionada] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalItensAberto, setModalItensAberto] = useState(false);
  const [showPreEmpenhoModal, setShowPreEmpenhoModal] = useState(false);
  const [preEmpenhoData, setPreEmpenhoData] = useState(null);
  const token = localStorage.getItem('token');
  const generatePdf = useAsyncPdf({ token });
  const [isLoading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const navigate = useNavigate();
  const vinculo = JSON.parse(localStorage.getItem('vinculo'));
  const user = JSON.parse(localStorage.getItem('user'));
  const { params: paramsEntidade } = useParametrosEntidade(vinculo?.idEntidade);
  const { setDefaultModal, showDefaultModal } = useDefaultModal();
  const { setConfirmModal, showConfirmModal } = useConfirmModal();
  const anoBase = parseInt(localStorage.getItem('anoBase'));

  useEffect(() => {
    if (!token) { navigate('/'); return; }
    if (!vinculo || !vinculo.idEntidade) {
      showDefaultModal('error', 'Selecione uma entidade antes de acessar esta página.');
      window.location.href = '/home';
      return;
    }
    setLoading(true);
    axios
      .get(`${API_BASE_URL.COMPRA_DIRETA}/entidade/${vinculo.idEntidade}/ano/${anoBase}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(result => setCompras(result.data))
      .catch(error => {
        console.error('Erro ao obter Compras Diretas:', error);
        showDefaultModal('error', 'Erro ao carregar Compras Diretas.');
      })
      .finally(() => setLoading(false));
  }, [atualizar]);

  function handleDelete(compra) {
    showConfirmModal('delete', () => handleConfirmDelete(compra));
  }

  function handleConfirmDelete(compra) {
    axios
      .delete(`${API_BASE_URL.COMPRA_DIRETA}/${compra.id}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(() => {
        showDefaultModal('success', 'Compra Direta excluída com sucesso!');
        setAtualizar(prev => !prev);
        setCompraSelecionada(null);
      })
      .catch(error => {
        console.error('Erro ao excluir Compra Direta:', error);
        showDefaultModal('error', 'Erro ao excluir a Compra Direta!');
      });
  }

  function handleEdit(compra) {
    setCompraSelecionada(compra);
    setShowModal(true);
  }

  function handleNew() {
    setCompraSelecionada(null);
    setShowModal(true);
  }

  function handleCloseModal() {
    setShowModal(false);
    setCompraSelecionada(null);
  }

  function handleSaveSuccess() {
    setAtualizar(prev => !prev);
    setCompraSelecionada(null);
  }

  function handleItensSaveSuccess() {
    setAtualizar(prev => !prev);
  }

  function downloadBlob(url, filename) {
    setGenerating(true);
    axios({
      url,
      method: 'GET',
      responseType: 'blob',
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(response => {
        const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = blobUrl;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(blobUrl);
      })
      .catch(error => {
        console.error('Erro ao baixar:', error);
        showDefaultModal('error', 'Erro ao gerar o documento.');
      })
      .finally(() => setGenerating(false));
  }

  function downloadWord(compra) {
    downloadBlob(`${API_BASE_URL.COMPRA_DIRETA}/${compra.id}/docx`, `CD_PEDIDO_${compra.id}.docx`);
  }

  function openPdf(compra) {
    const pdfId = `CD_PEDIDO_${compra.id}_${Date.now()}`;
    setGenerating(true);
    generatePdf({
      startFn: () => axios.get(`${API_BASE_URL.COMPRA_DIRETA}/${compra.id}/pdf`, { headers: { Authorization: `Bearer ${token}` } }),
      statusUrlFn: jobId => `${API_BASE_URL.COMPRA_DIRETA}/${compra.id}/pdfStatus/${jobId}`,
      fileUrlFn: jobId => `${API_BASE_URL.COMPRA_DIRETA}/${compra.id}/pdfFile/${jobId}`,
    })
      .then(({ blob }) => {
        const url = window.URL.createObjectURL(blob);
        if (!window.pdfStorage) window.pdfStorage = {};
        window.pdfStorage[pdfId] = blob;
        sessionStorage.setItem('pdfViewerData', JSON.stringify({ url, title: `CD_PEDIDO_${compra.id}`, pdfId }));
        window.dispatchEvent(new CustomEvent('pdfViewerUpdate', { detail: { pdfId } }));
      })
      .catch(error => {
        console.error('Erro ao abrir PDF:', error);
        showDefaultModal('error', error.message || 'Erro ao gerar o PDF.');
      })
      .finally(() => setGenerating(false));
  }

  function openPreEmpenhoPdf(id, num) {
    setGenerating(true);
    generatePdf({
      startFn: () => axios.get(`${API_BASE_URL.PRE_EMPENHO}/${id}/pdf`, { headers: { Authorization: `Bearer ${token}` } }),
      statusUrlFn: jobId => `${API_BASE_URL.PRE_EMPENHO}/pdfStatus/${jobId}`,
      fileUrlFn: jobId => `${API_BASE_URL.PRE_EMPENHO}/pdfFile/${jobId}`,
    })
      .then(({ blob, missingFields }) => {
        const url = window.URL.createObjectURL(blob);
        const pdfId = `pre_empenho_${id}_${Date.now()}`;
        if (!window.pdfStorage) window.pdfStorage = {};
        window.pdfStorage[pdfId] = blob;
        const title = `Pré-Empenho ${num || id}`;
        if (missingFields && missingFields.length > 0) {
          showDefaultModal('warning', missingFields.join('\n'), () => {
            sessionStorage.setItem('pdfViewerData', JSON.stringify({ url, title, pdfId }));
            window.dispatchEvent(new CustomEvent('pdfViewerUpdate', { detail: { pdfId } }));
          });
        } else {
          sessionStorage.setItem('pdfViewerData', JSON.stringify({ url, title, pdfId }));
          window.dispatchEvent(new CustomEvent('pdfViewerUpdate', { detail: { pdfId } }));
        }
      })
      .catch(err => {
        console.error('Erro ao gerar PDF do Pré-Empenho:', err);
        showDefaultModal('error', err.message || 'Erro ao gerar o PDF do Pré-Empenho.');
      })
      .finally(() => setGenerating(false));
  }

  function openPreEmpenhoWord(id) {
    setGenerating(true);
    axios
      .get(`${API_BASE_URL.PRE_EMPENHO}/${id}/docx`, {
        responseType: 'blob',
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(res => {
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const doDownload = () => {
          const link = document.createElement('a');
          link.href = url;
          link.download = `PRE_EMPENHO_${id}.docx`;
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
        };
        const missingHeader = res.headers['x-missing-fields'];
        if (missingHeader) {
          const fields = missingHeader.split(',');
          showDefaultModal('warning', fields.join('\n'), doDownload);
        } else {
          doDownload();
        }
      })
      .catch(err => {
        console.error('Erro ao baixar Word do Pré-Empenho:', err);
        showDefaultModal('error', 'Erro ao baixar o Word do Pré-Empenho.');
      })
      .finally(() => setGenerating(false));
  }

  function fetchPreEmpenho(compra, callback) {
    setGenerating(true);
    axios
      .get(`${API_BASE_URL.PRE_EMPENHO}/por-vinculo`, {
        params: { tipoVinculo: 'COMPRA_DIRETA', idVinculo: compra.id },
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(res => {
        if (res.data && res.data.id) {
          callback(res.data);
        } else {
          showDefaultModal('error', 'Nenhum Pré-Empenho encontrado para esta compra.');
          setGenerating(false);
        }
      })
      .catch(() => {
        showDefaultModal('error', 'Nenhum Pré-Empenho encontrado para esta compra.');
        setGenerating(false);
      });
  }

  function openPreEmpenho(compra) {
    axios
      .get(`${API_BASE_URL.PRE_EMPENHO}/por-vinculo`, {
        params: { tipoVinculo: 'COMPRA_DIRETA', idVinculo: compra.id },
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(res => {
        if (res.data && res.data.id) {
          setPreEmpenhoData(res.data);
        } else {
          setPreEmpenhoData({ objeto: compra?.objeto || '' });
        }
        setShowPreEmpenhoModal(true);
      })
      .catch(() => {
        setPreEmpenhoData({ objeto: compra?.objeto || '' });
        setShowPreEmpenhoModal(true);
      });
  }

  const normalizeString = str =>
    str.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toUpperCase();

  const comprasFiltradas = Array.isArray(compras)
    ? compras
        .filter(c => {
          const b = normalizeString(filtro);
          return (
            normalizeString(c.numCompra || '').includes(b) ||
            normalizeString(c.objeto || '').includes(b) ||
            normalizeString(c.fornecedor?.nome || '').includes(b)
          );
        })
        .sort((a, b) => (b.numCompra || '').localeCompare(a.numCompra || '', undefined, { numeric: true }))
    : [];

  const totalItems = comprasFiltradas.length;
  const { setPagination } = usePagination10(totalItems);
  const paginationRef = useState(null);
  const currentItems = comprasFiltradas.slice(setPagination.startIndex, setPagination.startIndex + setPagination.rowsPerPage);

  useEffect(() => {
    if (paginationRef.current && paginationRef.current.resetPage) {
      paginationRef.current.resetPage();
    }
  }, [filtro]);

  const formatDate = date => {
    if (!date) return '';
    if (Array.isArray(date)) {
      const [year, month, day] = date;
      return `${String(day).padStart(2, '0')}/${String(month).padStart(2, '0')}/${year}`;
    }
    const d = new Date(date);
    return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
  };

  return (
    <Layout>
      <ResponsiveShadow>
        <PageHeader title="Compras Diretas" icon="bi-cart" />
        <PageContent>
          <div className="d-flex flex-column flex-lg-row justify-content-lg-between align-items-start align-items-lg-center gap-2 mb-2">
            <div className="botao-grid-2col">
              <button className="btn btn-success btn-sm botao-igual" onClick={handleNew} title="Nova Compra Direta">
                <i className="bi bi-plus-circle me-1"></i>Nova
              </button>
              <button
                className="btn btn-primary btn-sm botao-igual"
                disabled={!compraSelecionada}
                onClick={() => handleEdit(compraSelecionada)}
                title={!compraSelecionada ? 'Selecione uma Compra' : 'Editar Compra selecionada'}
              >
                <i className="bi bi-pencil me-1"></i>Editar
              </button>
              {paramsEntidade.EDITAR_DPE !== 'N' && (
                <button
                  className="btn btn-primary btn-sm botao-igual"
                  disabled={!compraSelecionada || !compraSelecionada.itens?.length}
                  onClick={() => openPreEmpenho(compraSelecionada)}
                  title={!compraSelecionada?.itens?.length ? 'Compra sem itens' : 'Editar Pré-Empenho'}
                >
                  <i className="bi bi-pencil me-1"></i>Editar DPE
                </button>
              )}
              <button
                className="btn btn-danger btn-sm botao-igual"
                disabled={!compraSelecionada}
                onClick={() => handleDelete(compraSelecionada)}
                title={!compraSelecionada ? 'Selecione uma Compra' : 'Excluir Compra selecionada'}
              >
                <i className="bi bi-trash me-1"></i>Excluir
              </button>
              <div className="toolbar-separator" />
              <button
                className="btn btn-success btn-sm botao-igual"
                disabled={!compraSelecionada}
                onClick={() => setModalItensAberto(true)}
                title="Ver itens da Compra selecionada"
              >
                <i className="bi bi-list-ul me-1"></i>Itens da Compra
              </button>
                <Dropdown>
                  <Dropdown.Toggle variant="secondary" size="sm" disabled={!compraSelecionada || !compraSelecionada.itens?.length} className="botao-igual">
                    <i className="bi bi-eye me-1"></i>Visualizar
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    <div className="px-3 py-1 d-flex align-items-center justify-content-between gap-3" style={{ minWidth: 200 }}>
                      <span className="small fw-semibold">Pedido</span>
                      <div className="d-flex gap-1">
                        {paramsEntidade.VISUALIZAR_TIPO !== 'WORD' && (
                          <button className="btn btn-sm btn-outline-danger px-1 py-0" onClick={e => { e.stopPropagation(); openPdf(compraSelecionada); }} title="Abrir PDF do Pedido">
                            <i className="bi bi-file-pdf"></i>
                          </button>
                        )}
                        {paramsEntidade.VISUALIZAR_TIPO !== 'PDF' && (
                          <button className="btn btn-sm btn-outline-primary px-1 py-0" onClick={e => { e.stopPropagation(); downloadWord(compraSelecionada); }} title="Baixar Word do Pedido">
                            <i className="bi bi-file-word"></i>
                          </button>
                        )}
                      </div>
                    </div>
                    <Dropdown.Divider className="my-1" />
                    <div className="px-3 py-1 d-flex align-items-center justify-content-between gap-3" style={{ minWidth: 200 }}>
                      <span className="small fw-semibold">DPE</span>
                      <div className="d-flex gap-1">
                        {paramsEntidade.VISUALIZAR_TIPO !== 'WORD' && (
                          <button className="btn btn-sm btn-outline-danger px-1 py-0" onClick={e => { e.stopPropagation(); fetchPreEmpenho(compraSelecionada, d => openPreEmpenhoPdf(d.id, d.numPreEmpenho)); }} title="Abrir PDF do DPE">
                            <i className="bi bi-file-pdf"></i>
                          </button>
                        )}
                        {paramsEntidade.VISUALIZAR_TIPO !== 'PDF' && (
                          <button className="btn btn-sm btn-outline-primary px-1 py-0" onClick={e => { e.stopPropagation(); fetchPreEmpenho(compraSelecionada, d => openPreEmpenhoWord(d.id)); }} title="Baixar Word do DPE">
                            <i className="bi bi-file-word"></i>
                          </button>
                        )}
                      </div>
                    </div>
                  </Dropdown.Menu>
                </Dropdown>
            </div>
            <div className="d-flex align-items-center gap-2 flex-nowrap mt-2 mt-lg-0">
              <div className="input-group input-group-sm" style={{ minWidth: 150 }}>
                <span className="input-group-text d-flex align-items-center justify-content-center"><i className="bi bi-search"></i></span>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Buscar..."
                  title="Buscar por número, objeto, fornecedor..."
                  value={filtro}
                  onChange={e => setFiltro(e.target.value.toUpperCase())}
                />
              </div>
            </div>
          </div>

          <TableContainer isLoading={isLoading}>
            <DataTable style={{ fontSize: '0.8rem' }} head={
              <tr>
                <th scope="col" className="px-2">Nº CD</th>
                <th scope="col" className="px-2">Fornecedor</th>
                <th scope="col" className="px-2">Data</th>
                <th scope="col" className="px-2" style={{ minWidth: '200px' }}>Objeto</th>
                <th scope="col" className="px-2 text-center">Qtde Itens</th>
                <th scope="col" className="px-2 text-end">Valor Total</th>
              </tr>}>
              <tbody>
                {currentItems.map(compra => (
                  <tr
                    key={compra.id}
                    className={compraSelecionada && compraSelecionada.id === compra.id ? 'table-primary' : ''}
                    style={{ cursor: 'pointer' }}
                    onClick={() => setCompraSelecionada(compra)}
                    onDoubleClick={() => { setCompraSelecionada(compra); setModalItensAberto(true); }}
                  >
                    <td className="px-2 fw-semibold">{compra.idProprio || compra.numCompra}</td>
                    <td className="px-2">{compra.fornecedor?.nome || compra.idFornecedor}</td>
                    <td className="px-2">{formatDate(compra.data)}</td>
                    <td className="px-2">
                      <div style={{ whiteSpace: 'normal', wordBreak: 'break-word', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                        {compra.objeto}
                      </div>
                    </td>
                    <td className="px-2 text-center">{compra.itens?.length || 0}</td>
                    <td className="px-2 text-end">
                      {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
                        (compra.itens || []).reduce((total, item) => total + ((item.quantidade || 0) * (item.valorUnitario || 0)), 0)
                      )}
                    </td>
                  </tr>
                ))}
                {currentItems.length === 0 && (
                  <tr>
                    <td colSpan="6" className="text-center py-3 text-muted">
                      <i className="bi bi-inbox fs-3 d-block mb-2"></i>
                      Nenhuma Compra Direta encontrada
                    </td>
                  </tr>
                )}
              </tbody>
            </DataTable>
          </TableContainer>

          {showModal && (
            <CompraDiretaModal show={showModal} compra={compraSelecionada} onClose={handleCloseModal} onSave={handleSaveSuccess} />
          )}
        </PageContent>
        <div style={{ padding: '0.5rem 1rem', borderTop: '1px solid rgba(0,0,0,0.1)', backgroundColor: '#f8f9fa' }}>
          <Pagination10 {...setPagination} />
        </div>
        <ConfirmModal {...setConfirmModal} />
        <DefaultModal {...setDefaultModal} />
        <LoadingModal loading={generating} message="Gerando PDF, por favor aguarde..." />
        <CompraDiretaItensModal isOpen={modalItensAberto} onClose={() => setModalItensAberto(false)} compra={compraSelecionada} onSave={handleItensSaveSuccess} />
        <PreEmpenhoModal
          show={showPreEmpenhoModal}
          preEmpenho={preEmpenhoData}
          onClose={() => { setShowPreEmpenhoModal(false); setPreEmpenhoData(null); }}
          onSave={() => { setShowPreEmpenhoModal(false); setPreEmpenhoData(null); }}
        />
      </ResponsiveShadow>
    </Layout>
  );
}

export default ComprasDiretas;
